/**
 * Regras de exibição da auditoria — funções puras, sem React, testadas em
 * auditLogView.test.js. O que mostrar sai do CATÁLOGO do core-contracts
 * (estruturas, papéis, campos filtráveis): nenhuma ação tem código próprio aqui.
 */
import {
  AUDIT_DATA_FILTER_PREFIX,
  AUDIT_STRUCTS,
  auditFieldWithRole,
  auditFilterableFields,
  auditStructOf
} from "@markedesk/core-contracts";

/** Mesmo fuso do backend: o "dia" da auditoria é o de São Paulo. */
export const AUDIT_TIMEZONE = "America/Sao_Paulo";

/** "2026-09-24" do registro, no fuso da auditoria. */
export const dayOf = createdAt =>
  new Date(createdAt).toLocaleDateString("sv-SE", { timeZone: AUDIT_TIMEZONE });

/** Separa em dias, mantendo a ordem (a lista já vem do mais novo ao mais velho). */
export const groupByDay = records => {
  const groups = [];
  for (const record of records) {
    const day = dayOf(record.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.records.push(record);
    else groups.push({ day, records: [record] });
  }
  return groups;
};

const contextOf = record => record.meta?.context || {};
const dataOf = record => record.meta?.data || {};

/**
 * Quem fez: usuário, token de API ou o próprio sistema. Registro gravado sem
 * nome (token antigo, antes da correção do JWT) usa o nome ATUAL do usuário,
 * via `nameOf(id)` — melhor que só o id.
 */
export const actorOf = (record, nameOf = () => null) => {
  if (record.userId) {
    return { kind: "user", name: contextOf(record).userName || nameOf(record.userId) || null, id: record.userId };
  }
  if (record.tokenId) return { kind: "api", token: record.tokenId };
  return { kind: "system", source: record.source };
};

export const isSupport = record => record.support === true;

/** Alvo legível pelos papéis da estrutura: `title (subtitle)`, ou `entidade #id`. */
export const targetOf = (record, entityLabel = entity => entity) => {
  const data = dataOf(record);
  const titleField = auditFieldWithRole(record.action, "title");
  const subtitleField = auditFieldWithRole(record.action, "subtitle");
  const title = titleField ? data[titleField] : undefined;
  const subtitle = subtitleField ? data[subtitleField] : undefined;
  if (title) return subtitle ? `${title} (${subtitle})` : String(title);
  if (record.entityId) return `${entityLabel(record.action.split(".")[0])} #${record.entityId}`;
  return "";
};

/**
 * Campos do `data` na ordem da estrutura, com a chave de tradução do rótulo.
 * `ref` vira o nome copiado (ou #id). Campo fora da estrutura não aparece.
 */
export const dataFieldsOf = record => {
  const struct = auditStructOf(record.action);
  if (!struct) return [];
  const data = dataOf(record);
  return Object.entries(AUDIT_STRUCTS[struct].fields)
    .filter(([field, def]) => def.kind !== "secret" && data[field] !== undefined)
    .map(([field, def]) => {
      const value = data[field];
      const text = def.kind === "ref" ? value?.name || `#${value?.id}` : String(value);
      return { field, labelKey: `auditLogs.fields.${struct}.${field}`, value: text, pii: !!def.pii };
    });
};

/** Circunstância do request (o que o `meta.context` tiver). */
export const contextFieldsOf = record => {
  const { ip, userAgent, route, serverId, actorCompanyId } = contextOf(record);
  return { ip, userAgent, route, serverId, actorCompanyId };
};

/** Valores para interpolar na frase da ação: autor + campos do `data`. */
export const sentenceValuesOf = (record, actorText) => ({ ...dataOf(record), actor: actorText });

const MARK = "\u0001";

/**
 * Frase traduzida quebrada em partes, com os valores interpolados marcados
 * para destaque (autor, alvo): o JSX põe `<strong>` sem montar HTML.
 * `translate(key, values)` é o i18n.t — injetado para manter isto puro.
 */
export const sentenceParts = (translate, key, values) => {
  const markers = Object.fromEntries(Object.keys(values).map(name => [name, `${MARK}${name}${MARK}`]));
  const text = translate(key, { ...markers, interpolation: { escapeValue: false } });
  return text
    .split(new RegExp(`${MARK}(\\w+)${MARK}`))
    .map((chunk, i) => (i % 2 === 1 ? { text: String(values[chunk] ?? ""), strong: true } : { text: chunk, strong: false }))
    .filter(part => part.text !== "");
};

/** Iniciais para o avatar: "Eliana Souza" → "ES"; um nome só → 2 primeiras letras. */
export const initialsOf = name => {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .map(word => word.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

/**
 * Resposta vazia com continuação = a busca parou no teto de blocos sem achar
 * nada. A tela diz até onde procurou e oferece continuar.
 */
export const searchStoppedEarly = (records, nextCursor) => records.length === 0 && !!nextCursor;

/** Campos que a tela oferece como filtro quando esta ação está escolhida. */
export const dataFilterFieldsOf = action => {
  if (!action) return [];
  const struct = auditStructOf(action);
  return auditFilterableFields(action).map(field => ({
    field,
    labelKey: `auditLogs.fields.${struct}.${field}`
  }));
};

/**
 * Filtros da tela → params da rota: sem vazio, `support` só quando ligado, e
 * `data` achatado em `data.<campo>` (formato da query string do contrato).
 */
export const toQueryParams = filters => {
  const { data, ...plain } = filters || {};
  const params = {};
  for (const [key, value] of Object.entries(plain)) {
    if (value === undefined || value === null || value === "" || value === false) continue;
    params[key] = value;
  }
  if (params.action) {
    for (const [field, value] of Object.entries(data || {})) {
      if (value) params[`${AUDIT_DATA_FILTER_PREFIX}${field}`] = value;
    }
  }
  return params;
};
